import React from "react";
import { Grid, Card, CircularProgress } from "@material-ui/core";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "./InfoTooltip";
import { useDisplay } from "context/DisplayContext";

const RetentionMetricsCard = ({ metrics, loading, error, filter }) => {
  const { isMobile } = useDisplay();

  if (loading) {
    return (
      <Card style={{ padding: 20, textAlign: "center" }}>
        <CircularProgress />
        <PrimaryText>Loading retention metrics...</PrimaryText>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20, textAlign: "center" }}>
        <PrimaryText color="error">Error loading metrics: {error}</PrimaryText>
      </Card>
    );
  }

  const dailyMetrics = metrics?.dailyMetrics || {};
  const filteredMetrics = metrics?.filteredMetrics || {};

  // For weekly view, show weekly averages
  const getRetentionValue = (metric) => {
    if (filter === "weekly" && filteredMetrics.weeklyRetention) {
      return filteredMetrics.weeklyRetention[metric] || 0;
    }
    return dailyMetrics[metric] || 0;
  };

  return (
    <Card style={{ padding: isMobile ? 12 : 20 }}>
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Day 1 Retention */}
        <Grid item xs={12} sm={6} md={3}>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
          >
            <PrimaryText fontSize={isMobile ? 14 : 16} fontWeight={600}>
              Day 1 Retention
            </PrimaryText>
            <InfoTooltip
              title="Day 1 Retention"
              content="Percentage of users who return the day after signup. This is a critical early indicator of user satisfaction."
            />
          </div>
          <PrimaryText fontSize={isMobile ? 22 : 28} fontWeight={700}>
            {getRetentionValue("day1Retention")
              ? (getRetentionValue("day1Retention") * 100).toFixed(1) + "%"
              : "N/A"}
          </PrimaryText>
          <PrimaryText fontSize={12} style={{ color: "#666", marginTop: 4 }}>
            Target: {">"}60%
          </PrimaryText>
        </Grid>

        {/* Day 7 Retention */}
        <Grid item xs={12} sm={6} md={3}>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
          >
            <PrimaryText fontSize={isMobile ? 14 : 16} fontWeight={600}>
              Day 7 Retention
            </PrimaryText>
            <InfoTooltip
              title="Day 7 Retention"
              content="Percentage of users still active after one week. Indicates if users find ongoing value in the app."
            />
          </div>
          <PrimaryText fontSize={isMobile ? 22 : 28} fontWeight={700}>
            {getRetentionValue("day7Retention")
              ? (getRetentionValue("day7Retention") * 100).toFixed(1) + "%"
              : "N/A"}
          </PrimaryText>
          <PrimaryText fontSize={12} style={{ color: "#666", marginTop: 4 }}>
            Target: {">"}30%
          </PrimaryText>
        </Grid>

        {/* Day 30 Retention */}
        <Grid item xs={12} sm={6} md={3}>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
          >
            <PrimaryText fontSize={isMobile ? 14 : 16} fontWeight={600}>
              Day 30 Retention
            </PrimaryText>
            <InfoTooltip
              title="Day 30 Retention"
              content="Percentage of users still active after one month. Strong indicator of long-term user value."
            />
          </div>
          <PrimaryText fontSize={isMobile ? 22 : 28} fontWeight={700}>
            {getRetentionValue("day30Retention")
              ? (getRetentionValue("day30Retention") * 100).toFixed(1) + "%"
              : "N/A"}
          </PrimaryText>
          <PrimaryText fontSize={12} style={{ color: "#666", marginTop: 4 }}>
            Target: {">"}15%
          </PrimaryText>
        </Grid>

        {/* DAU/MAU Ratio */}
        <Grid item xs={12} sm={6} md={3}>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
          >
            <PrimaryText fontSize={isMobile ? 14 : 16} fontWeight={600}>
              DAU/MAU Ratio
            </PrimaryText>
            <InfoTooltip
              title="DAU/MAU Ratio"
              content="Stickiness metric showing daily vs monthly engagement. Higher ratios indicate more engaged users."
            />
          </div>
          <PrimaryText fontSize={isMobile ? 22 : 28} fontWeight={700}>
            {dailyMetrics.dauMauRatio || "N/A"}
          </PrimaryText>
          <PrimaryText fontSize={12} style={{ color: "#666", marginTop: 4 }}>
            Target: {">"}0.3
          </PrimaryText>
        </Grid>
      </Grid>
    </Card>
  );
};

export default RetentionMetricsCard;
